import React from 'react';
import { ArrowLeft, MoreVertical, Users, BellOff, Bell, Trash2, LogOut, Ban, X, Hash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { UnifiedConversation } from '@/types/unified-messaging';
import { useAppTranslations } from '@/hooks/useAppTranslations';
import { formatDistanceToNow } from 'date-fns';
import { fr, nl } from 'date-fns/locale';

interface ConversationHeaderProps {
  conversation: UnifiedConversation | null;
  conversationType: 'channel' | 'direct';
  isOnline: boolean;
  offlineMessagesCount: number;
  isRetrying: boolean;
  showSearch: boolean;
  onBack: () => void;
  onShowGroupInfo: () => void;
  onShowMoreMenu: () => void;
  onToggleSearch: () => void;
  showMoreMenu: boolean;
  isMuted: boolean;
  onMuteNotifications: () => void;
  onClearChat: () => void;
  onBlockOrLeave: () => void;
  language: string;
  currentUserId?: string;
  /** When provided, replaces the derived conversation title in the header */
  overrideName?: string;
  /** When true, suppresses safe-area-top padding (embedded inside a non-top-level container) */
  embedded?: boolean;
  /** When provided, renders a Hash button after the back button to toggle the channel sidebar */
  onChannelSidebarToggle?: () => void;
}

export function ConversationHeader({
  conversation,
  conversationType,
  isOnline,
  offlineMessagesCount,
  isRetrying,
  showSearch,
  onBack,
  onShowGroupInfo,
  onShowMoreMenu,
  onToggleSearch,
  showMoreMenu,
  isMuted,
  onMuteNotifications,
  onClearChat,
  onBlockOrLeave,
  language,
  currentUserId,
  overrideName,
  embedded = false,
  onChannelSidebarToggle,
}: ConversationHeaderProps) {
  const { t } = useAppTranslations();
  const navigate = useNavigate();

  const formatDateWithLocale = (date: Date) => {
    const localeMap = { fr, nl, en: undefined };
    return formatDistanceToNow(date, { addSuffix: true, locale: localeMap[language as keyof typeof localeMap] });
  };

  // For coaching conversations, detect if current user is the coach
  // description formats:
  //   legacy: "coaching:coach:{coachUserId}"
  //   new: "coaching:coach:{coachUserId}:player:{playerUserId}"
  const isCoachViewingOwnConversation = conversation?.description?.startsWith(`coaching:coach:${currentUserId}`);

  const getOtherParticipantName = () => {
    const participants = conversation?.participants || [];
    return participants.find(p => p.id !== currentUserId)?.display_name || null;
  };

  const getConversationTitle = () => {
    if (overrideName) return overrideName;
    if (!conversation) return t.messaging.loading;

    if (conversation.type === 'channel') {
      return `#${conversation.title || conversation.display_name || 'Unknown Channel'}`;
    } else if (conversation.type === 'group') {
      // Coach viewing their own coaching conversation → show student name
      if (isCoachViewingOwnConversation) {
        return getOtherParticipantName() || conversation.title || conversation.display_name || t.messaging.groupChatTitle;
      }
      return getOtherParticipantName() || conversation.title || conversation.display_name || t.messaging.groupChatTitle;
    } else {
      const participants = conversation.participants || [];
      const otherParticipant = participants.find(p => p.id !== currentUserId);
      return otherParticipant?.display_name || 'Direct Message';
    }
  };

  const getConversationAvatar = () => {
    if (!conversation) return null;

    if (conversation.type === 'channel') {
      return conversation.avatar_url;
    } else {
      const participants = conversation.participants || [];
      const otherParticipant = participants.find(p => p.id !== currentUserId);
      return otherParticipant?.avatar_url;
    }
  };

  const getConversationInitials = () => {
    if (!conversation) return '?';

    if (conversation.type === 'channel') {
      return (conversation.title || conversation.display_name || 'C').charAt(0).toUpperCase();
    } else if (conversation.type === 'group') {
      // Coach viewing their own coaching conversation → show student initials
      if (isCoachViewingOwnConversation) {
        const name = getOtherParticipantName();
        if (name) return name.charAt(0).toUpperCase();
      }
      return (conversation.title || conversation.display_name || 'G').charAt(0).toUpperCase();
    } else {
      const participants = conversation.participants || [];
      const otherParticipant = participants.find(p => p.id !== currentUserId);
      return (otherParticipant?.display_name || 'D').charAt(0).toUpperCase();
    }
  };

  const handleAvatarClick = () => {
    if (conversationType === 'direct' && conversation?.participants) {
      const otherParticipant = conversation.participants.find(p => p.id !== currentUserId);
      if (otherParticipant) {
        navigate(`/profile/${otherParticipant.id}`);
      }
    } else if (conversationType === 'channel' && conversation?.club_id) {
      navigate(`/club/${conversation.club_id}`);
    }
  };

  return (
    <>
    <div
      className={`px-2 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2${embedded ? '' : ' safe-area-top'}`}
      style={{
        background: 'rgba(255,255,255,0.82)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: '1px solid rgba(15,23,42,0.08)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 2px 8px rgba(15,23,42,0.06)',
      }}
    >
      <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
        <button
          onClick={onBack}
          className="h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center rounded-xl shrink-0 transition-all duration-150 hover:scale-105 active:scale-95"
          style={{
            background: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(15,23,42,0.08)',
            boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 2px 6px rgba(15,23,42,0.06)',
          }}
        >
          <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 text-slate-700" />
        </button>

        {onChannelSidebarToggle && (
          <button
            onClick={onChannelSidebarToggle}
            aria-label="Channels"
            className="h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center rounded-xl shrink-0 transition-all duration-150 hover:scale-105 active:scale-95"
            style={{
              background: 'rgba(255,255,255,0.7)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: '1px solid rgba(15,23,42,0.08)',
              boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 2px 6px rgba(15,23,42,0.06)',
            }}
          >
            <Hash className="h-4 w-4 sm:h-5 sm:w-5 text-slate-700" />
          </button>
        )}

        <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
          <div
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full overflow-hidden flex items-center justify-center text-white text-xs sm:text-sm font-bold shadow-md cursor-pointer hover:scale-105 transition-all duration-200 shrink-0"
            style={{
              background: getConversationAvatar()
                ? `url(${getConversationAvatar()}) center/cover`
                : 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)'
            }}
            onClick={handleAvatarClick}
          >
            {!getConversationAvatar() && getConversationInitials()}
          </div>

          <div
            className="cursor-pointer rounded-lg px-1 sm:px-2 py-1 -ml-1 sm:-ml-2 transition-all duration-200 min-w-0"
            onClick={handleAvatarClick}
          >
            <h2 className="font-bold text-slate-900 text-sm sm:text-[15px] tracking-tight truncate max-w-[120px] sm:max-w-none">
              {getConversationTitle()}
            </h2>
            <p className="text-xs text-slate-500 flex items-center truncate">
              {conversationType === 'channel' ? (
                <>
                  {t.messaging.membersCount.replace('{count}', (conversation?.participants?.length || 0).toString())}
                </>
              ) : conversation?.type === 'group' ? (
                <>
                  {t.messaging.participantsCount.replace('{count}', (conversation?.participants?.length || 0).toString())}
                  {conversation?.last_message_at && (
                    <>
                      <span className="hidden sm:inline">
                        {' • '}
                        {formatDateWithLocale(new Date(conversation.last_message_at))}
                      </span>
                    </>
                  )}
                </>
              ) : (
                <>
                  {conversation?.last_message_at && formatDateWithLocale(new Date(conversation.last_message_at))}
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-1.5 shrink-0">
        {!isOnline && (
          <span className="text-[10px] sm:text-xs text-red-500 bg-red-100 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-medium">
            Offline
          </span>
        )}
        {offlineMessagesCount > 0 && (
          <span className="text-[10px] sm:text-xs text-orange-600 bg-orange-100 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-medium hidden sm:inline">
            {offlineMessagesCount} queued
          </span>
        )}
        {isRetrying && (
          <span className="text-[10px] sm:text-xs text-blue-600 bg-blue-100 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-medium">
            <span className="hidden sm:inline">Sending...</span>
            <span className="sm:hidden">...</span>
          </span>
        )}

        {/* Search button */}
        <button
          onClick={onToggleSearch}
          className="h-9 w-9 flex items-center justify-center rounded-xl transition-all duration-150 hover:scale-105 active:scale-95"
          style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(15,23,42,0.08)', boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 2px 6px rgba(15,23,42,0.06)' }}
        >
          <svg className="h-4 w-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </button>

        {/* Group Info Button for Group Chats */}
        {conversation?.type === 'group' && (
          <button
            onClick={onShowGroupInfo}
            className="h-9 w-9 flex items-center justify-center rounded-xl transition-all duration-150 hover:scale-105 active:scale-95"
            style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(15,23,42,0.08)', boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 2px 6px rgba(15,23,42,0.06)' }}
          >
            <Users className="h-4 w-4 text-slate-600" />
          </button>
        )}

        {/* More menu button */}
        <div className="relative">
          <button
            onClick={onShowMoreMenu}
            className="h-9 w-9 flex items-center justify-center rounded-xl transition-all duration-150 hover:scale-105 active:scale-95"
            style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(15,23,42,0.08)', boxShadow: '0 1px 0 rgba(255,255,255,0.9) inset, 0 2px 6px rgba(15,23,42,0.06)' }}
            data-more-menu
          >
            <MoreVertical className="h-4 w-4 text-slate-600" />
          </button>

        </div>
      </div>
    </div>

    {/* More menu - Bottom Sheet (outside header to avoid backdropFilter stacking context breaking fixed) */}
    {showMoreMenu && (
      <>
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          onClick={onShowMoreMenu}
          style={{ animation: 'fadeIn 0.2s ease-out' }}
        />

        {/* Bottom Sheet */}
        <div
          className="fixed inset-x-0 bottom-0 z-50"
          style={{ animation: 'slideUpSheet 0.3s ease-out' }}
        >
          <div
            className="rounded-t-3xl overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.96)',
              backdropFilter: 'blur(32px) saturate(180%)',
              WebkitBackdropFilter: 'blur(32px) saturate(180%)',
              boxShadow: '0 -4px 32px rgba(15,23,42,0.16), 0 -1px 0 rgba(255,255,255,0.8) inset',
            }}
          >
            {/* Drag Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-slate-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 pb-4">
              <h3 className="text-[15px] font-bold text-slate-900 tracking-tight">Options</h3>
              <button
                onClick={onShowMoreMenu}
                className="h-8 w-8 tap-target inline-flex items-center justify-center rounded-xl transition-colors shrink-0"
                style={{ background: 'rgba(15,23,42,0.06)', border: '1px solid rgba(15,23,42,0.08)' }}
              >
                <X className="h-4 w-4 text-slate-600" />
              </button>
            </div>

            {/* Menu Items */}
            <div className="px-4 space-y-2" style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom))' }}>
              {/* Mute Notifications */}
              <button
                onClick={onMuteNotifications}
                className="w-full flex items-center gap-4 p-4 rounded-2xl transition-colors hover:bg-slate-50/80"
                style={{ background: 'rgba(15,23,42,0.03)', border: '1px solid rgba(15,23,42,0.06)' }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2A4494 0%, #1E3270 100%)' }}>
                  {isMuted ? (
                    <Bell className="w-5 h-5 text-white" />
                  ) : (
                    <BellOff className="w-5 h-5 text-white" />
                  )}
                </div>
                <span className="text-slate-800 font-medium">
                  {isMuted ? t.messaging.unmuteNotifications : t.messaging.muteNotifications}
                </span>
              </button>

              {/* Clear Chat */}
              <button
                onClick={onClearChat}
                className="w-full flex items-center gap-4 p-4 rounded-2xl transition-colors hover:bg-slate-50/80"
                style={{ background: 'rgba(15,23,42,0.03)', border: '1px solid rgba(15,23,42,0.06)' }}
              >
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-orange-600" />
                </div>
                <span className="text-slate-800 font-medium">{t.messaging.clearChat}</span>
              </button>

              {/* Block User / Leave Channel */}
              <button
                onClick={onBlockOrLeave}
                className="w-full flex items-center gap-4 p-4 rounded-2xl transition-colors hover:bg-red-50"
                style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.12)' }}
              >
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                  {conversationType === 'direct' ? (
                    <Ban className="w-5 h-5 text-red-600" />
                  ) : (
                    <LogOut className="w-5 h-5 text-red-600" />
                  )}
                </div>
                <span className="text-red-600 font-medium">
                  {conversationType === 'direct' ? t.messaging.blockUser : t.messaging.leaveChannel}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Animations */}
        <style>{`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes slideUpSheet { from { transform: translateY(100%); } to { transform: translateY(0); } }
        `}</style>
      </>
    )}
    </>
  );
}

